<template>
  <main
    class="product-detail"
    data-page-type="shop"
  >
    <GridContainer
      class="product-detail__header"
      padding
    >
      <div class="product-detail__title-block">
        <CdnImage
          v-if="brand?.logo"
          :class="[
            'product-detail__logo-image',
            'filter-invert ',
            `product-detail__logo-image--${brand.slug}`,
          ]"
          render-without-figure
          :src="brand.logo"
          :image-style="`aspect-ratio: ${brand.aspectRatio}`"
          :alt="`${brand.name} Logo`"
        />
        <Heading
          align="center"
          size="xl"
          tag="h1"
          class="product-detail__title"
        >
          {{ offer.product.family.name }}
          <span class="text--center text--default product-detail__variant">
            {{ offer.product.variantName }}
          </span>
        </Heading>
      </div>
      <SquareBadge
        :offer="offer"
        class="product-detail__badge"
      />
      <ImageGalleryContainer
        v-if="hasRelevantImages"
        :media-data="relevantImages"
        class="product-detail__image-gallery"
      />
      <div class="product-detail__offer">
        <TreueshopIcon
          v-if="hasTreueshopIcon"
          class="product-detail__treueshop-icon"
        />

        <OfferConfigurator
          :bundle-items="transformedAlternatives"
          :focused-item-index="initialFocusesItemIndex"
          class="product-detail__variants"
          @open-bundle-item-config="openBundleItemConfig"
          @select-variant="handleVariantChange"
        />

        <div class="product-detail__offer-details">
          <EnergyLabel
            :product="offer.product"
            class="product-detail__energy-label"
            datasheet-alignment="left"
          />
          <KameraLink :offer="offer" />
          <PricesContainer
            :legal-texts="offer.product.legalTexts"
            :prices-data="offerPrices"
            class="product-detail__prices"
          />
          <FinancingCalculator
            v-if="hasZeroPercentFinancing"
            :price="defaultPrice * 100"
            visual="product-page"
            is-collapsable
          />
          <div
            v-if="telekomContractLink"
            class="product-detail__buy-with-contract"
          >
            <LinkButton
              tag="a"
              is-external
              text
              :href="telekomContractLink"
              tealium-name="zu-den-details"
              :tealium-suffix="offer.product.articleNumber"
            >
              {{ contractLinkText }}
            </LinkButton>
          </div>
          <AddToCartPromptContainer
            :offer="offer"
            class="product-detail__add-to-cart-prompt"
          />
        </div>

        <div class="product-detail__availability">
          <AvailabilityInformationContainer
            :article-number="offer.product.articleNumber"
            :availability="offer.product.availability"
            is-presale
          />
          <FulfillerInfo
            v-if="provider"
            :provider="provider"
            :is-freight-forwarding-service="isFreightForwardingService"
          />
        </div>
      </div>
      <CompatibilityQuickView
        class="product-detail__compatibility"
        :offer="offer"
      />
    </GridContainer>

    <GridContainer padding>
      <Heading
        align="center"
        size="lg"
        tag="h2"
        spaces-bottom
      >
        {{ offer.product.family.name }} auf einen Blick
      </Heading>

      <ProductTabs :offer="offer" />
      <!-- Product is bundle, the Bundle may have a description -->
      <ContentToggle
        v-if="offer.product.description && offer.product.bundle"
        class="grid__item--spacing-2 grid__item--l-spacing-3"
      >
        <div
          class="product-detail__description"
          v-html="offer.product.description"
        ></div>
      </ContentToggle>
    </GridContainer>

    <section
      v-if="
        (accessoryOffers.length || recommendedOffers.length) &&
        !$config.public.performanceMode.enabled
      "
      class="grid__item--spacing-2 grid__item--m-spacing-1"
    >
      <GridContainer padding>
        <AccordionItem
          v-if="accessoryOffers.length"
          is-open-on-desktop
          is-small
        >
          <template #title>
            <Heading
              align="center"
              class="accordion-legacy__headline"
              size="lg"
              spaces-bottom
              tag="h2"
            >
              Passendes Zubehör
            </Heading>
          </template>
          <template #content>
            <OfferTiles
              data-test-id="accessory"
              :offers="accessoryOffers"
            />
          </template>
        </AccordionItem>
        <AccordionItem
          v-if="recommendedOffers.length"
          is-open-on-desktop
          is-small
        >
          <template #title>
            <Heading
              align="center"
              class="accordion-legacy__headline"
              size="lg"
              tag="h2"
              spaces-bottom
            >
              Das könnte Dich auch interessieren
            </Heading>
          </template>
          <template #content>
            <OfferTiles
              data-test-id="recommendation"
              :offers="recommendedOffers"
            />
          </template>
        </AccordionItem>
      </GridContainer>
    </section>

    <VisitedOfferTiles
      v-if="lastSeenOffersLength > 1"
      :max-size="4"
      :viewed-article-number="offer.product.articleNumber"
      has-grid-padding
    />
    <CartSticky
      :prices="offerPrices"
      class="product-detail__cart-sticky"
    >
      <template #addToCart>
        <AddToCartButtonContainer
          :display-cart-quantity="true"
          :display-text="false"
          :offer="offer"
          theme="light"
        />
      </template>
    </CartSticky>
  </main>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { defineAsyncComponent } from '#imports';
import { getPrice } from '@i22/rocket';
import { getPricesFromOfferWithRebate } from '@/lib/price-from-offer';
import { getTelekomContractLink, getTelekomContractLinkText } from '~/lib/telekom-contract-link';
import { getBrand } from '@/lib/brand';
import CdnImage from '@/components/cdn-image.vue';

import {
  Heading,
  GridContainer,
  OfferConfigurator,
  CartSticky,
  AccordionItem,
  EnergyLabel,
  LinkButton,
} from '@i22-td-smarthome/component-library';

// container buis logic: component library implemented
import PricesContainer from '@/components/product-detail/prices-container.vue';
import AvailabilityInformationContainer from '@/components/product-detail/availability-information-container.vue';
import ImageGalleryContainer from '@/components/product-detail/image-gallery-container.vue';
import AddToCartPromptContainer from '@/components/product-detail/add-to-cart-prompt-container.vue';
// internal components
import FulfillerInfo from '@/components/product-detail/fulfiller-info.vue';
import OfferTiles from '~/components/offer-tiles-container.vue';
import ProductTabs from '~/components/product-detail/tabs/product-tabs.vue';
import ContentToggle from '@/components/content-toggle.vue';
import AddToCartButtonContainer from '@/components/product-detail/add-to-cart-button-container.vue';
import FinancingCalculator from '@/components/financing-calculator.vue';
import CompatibilityQuickView from '@/components/compatibility-quick-view.vue';
import { getImagesFromOffer } from '@/lib/goliath-media';
import TreueshopIcon from '~/components/treueshop-icon.vue';
import SquareBadge from '~/components/product-detail/square-badge.vue';
import KameraLink from '~/components/kamera-link.vue';
import { trackFBEvent } from '~/lib/tracking-fb';

export default {
  name: 'ProductDetail',
  components: {
    LinkButton,
    EnergyLabel,
    AccordionItem,
    KameraLink,
    SquareBadge,
    TreueshopIcon,
    FinancingCalculator,
    Heading,
    GridContainer,
    OfferConfigurator,
    PricesContainer,
    AvailabilityInformationContainer,
    ImageGalleryContainer,
    AddToCartPromptContainer,
    FulfillerInfo,
    ContentToggle,
    ProductTabs,
    OfferTiles,
    VisitedOfferTiles: defineAsyncComponent(() => import('@/components/visited-offer-tiles.vue')),
    CartSticky,
    AddToCartButtonContainer,
    CompatibilityQuickView,
    CdnImage,
  },
  props: {
    offer: { type: Object, default: () => ({}) },
    transformedAlternatives: { type: Array, default: () => [] },
    additionalImages: { type: Array, default: () => [] }, // type: LocalImage from import { type LocalImage } from '@i22/rocket';
  },
  data() {
    return {
      initialFocusesItemIndex: parseInt(this.$route.hash.replace(/#/, ''), 10) || undefined,
      focusedItemIndex: undefined,
    };
  },
  computed: {
    ...mapGetters({
      lastSeenOffers: 'last-seen-offers/lastSeenOffersArticleNumbers',
    }),
    ...mapGetters('coupon', [
      'couponInfo',
      'isValidCoupon',
      'incompatibleArticleNumbers',
      'compatibleArticleNumbers',
      'compatibleProductProviders',
    ]),
    ...mapGetters('offers', ['rebates', 'zeroPercentFinancing']),
    hasZeroPercentFinancing() {
      return this.zeroPercentFinancing.includes(this.offer.product.articleNumber);
    },
    defaultPrice() {
      return getPrice(this.offerPrices, 'DEFAULT', 'ONETIME')?.value;
    },
    offerPrices() {
      return getPricesFromOfferWithRebate(this.offer, this.rebate);
    },
    relevantImages() {
      return [...this.additionalImages, ...getImagesFromOffer(this.offer)];
    },
    hasRelevantImages() {
      return this.relevantImages.length > 0;
    },
    accessoryOffers() {
      const { accessories } = this.offer;

      if (!accessories) {
        return [];
      }

      return accessories.slice(0, 4);
    },
    recommendedOffers() {
      const { recommendations } = this.offer;

      if (!recommendations) {
        return [];
      }

      return recommendations.slice(0, 4);
    },
    lastSeenOffersLength() {
      return this.lastSeenOffers.length;
    },
    rebate() {
      return this.rebates[this.offer.product.articleNumber];
    },
    telekomContractLink() {
      return getTelekomContractLink(this.offer);
    },
    hasSmartphoneCategory() {
      const productCategories = this.offer.product.family.categories;
      return productCategories.some((category) => category.categoryNumber === 'T_EGHANDYS');
    },
    contractLinkText() {
      return getTelekomContractLinkText(null, this.hasSmartphoneCategory);
    },
    isCouponCompatible() {
      if (this.compatibleArticleNumbers?.length > 0) {
        return this.compatibleArticleNumbers.includes(this.offer.product.articleNumber);
      }
      if (this.compatibleProductProviders?.length > 0) {
        return this.compatibleProductProviders.includes(this.offer.product.availability.provider);
      }
      if (this.incompatibleArticleNumbers?.length > 0) {
        return !this.incompatibleArticleNumbers.includes(this.offer.product.articleNumber);
      }
      return true;
    },
    allowDiscountedProduct() {
      if (this.couponInfo.type === 'coupon') {
        return !this.offer.prices.some((price) => price.type.toLowerCase() === 'was');
      }
      return true;
    },
    hasTreueshopIcon() {
      return this.isCouponCompatible && this.isValidCoupon && this.allowDiscountedProduct;
    },
    provider() {
      return this.offer.product?.availability?.provider || undefined;
    },
    // Samsung TV will be shipped by Freight Forwarding
    isFreightForwardingService() {
      return ['8806095474939', '8806095593715', '8806095593692'].includes(
        this.offer.product.articleNumber
      );
    },
    brand() {
      return getBrand(this.offer);
    },
  },
  mounted() {
    this.addOfferToLastSeen();
    trackFBEvent('ViewContent', {
      content_ids: [this.offer.product.articleNumber],
      content_name: this.offer.product.name,
      content_type: 'product',
      currency: 'EUR',
    });
  },
  beforeMount() {
    document.body.classList.add('sticky-cart');
  },
  beforeDestroy() {
    document.body.classList.remove('sticky-cart');
  },
  methods: {
    ...mapActions('last-seen-offers', ['initLastSeenOffers']),
    handleVariantChange({ variant }) {
      const index = this.focusedItemIndex || this.initialFocusesItemIndex;
      // Catch exception to avoid NavigationDuplicated issue
      this.$router.replace(`${variant.path}${index ? `#${index}` : ''}`).catch(() => {});
    },
    addOfferToLastSeen() {
      this.initLastSeenOffers({
        articleNumber: this.offer.product.articleNumber,
      });
    },
    openBundleItemConfig({ position }) {
      this.focusedItemIndex = position;
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;
@use 'assets/selectors/pattern/lists';

.product-detail {
  padding-top: 1.5 * $grid-base;

  &__header {
    margin-bottom: $grid-base * 4;

    @include mq($mq-md) {
      display: grid;
      grid-gap: 0 $grid-base * 2;
      grid-template-columns: 1fr 1fr;
      grid-template-areas:
        'header header'
        'badge badge'
        'image offer'
        'compatibility offer';
      margin-bottom: $grid-base * 10;
    }
  }

  &__title-block {
    margin-bottom: 1rem;
    grid-area: header;
  }

  &__badge {
    margin-bottom: 1.5rem;
    grid-area: badge;
  }

  &__compatibility {
    padding-top: $grid-base * 2;
    grid-area: compatibility;
  }

  &__offer {
    grid-area: offer;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &__variants {
    :deep(.single-item-configurator__variant-group),
    :deep(.bundle-configurator) {
      padding-bottom: 0;
      margin-bottom: $grid-base * 2;
    }
  }

  &__image-gallery {
    grid-area: image;
    @include mq(false, $mq-md - 1) {
      margin-bottom: 2 * $grid-base;
    }
  }

  &__logo-image {
    height: 20px;
    display: block;
    margin: 0 auto $grid-base;
  }

  &__buy-with-contract {
    margin-bottom: $grid-base;
    @include mq($mq-sm) {
      margin-bottom: $grid-base * 2;
    }
  }

  &__energy-label {
    --max-list-width: 220px;
    margin-bottom: $grid-base;
    @include font-size(36px, 36px);
    @include mq($mq-sm) {
      --max-list-width: none;
      margin-bottom: 2 * $grid-base;
      @include font-size(48px, 48px);
    }
  }

  :deep(.youtube-video) {
    margin-top: 1.5 * $grid-base;
    margin-bottom: 0;

    @include mq($mq-md) {
      margin-top: 0;
    }
  }

  &__description {
    columns: 456px 3;
    column-gap: 24px;

    @include mq($mq-sm) {
      margin-bottom: 4 * $grid-base;
    }

    :deep(ul) {
      margin-bottom: 1.5 * $grid-base;
      @extend .list;

      li {
        @extend .list__element;
        @extend .list__element--dashed;
      }
    }
  }

  &__variant {
    display: block;
    margin-bottom: 0;
    @include mq(false, $mq-md - 1) {
      display: none;
    }
  }

  &__cart-sticky {
    z-index: $z-index-cart-sticky;
    @include mq($mq-md) {
      visibility: hidden;
    }
  }

  &__availability {
    margin-bottom: $grid-base;
  }

  &__treueshop-icon {
    display: block;
    width: 50%;
    align-self: flex-end;
    margin-right: $grid-base;
    max-width: 250px;

    @include mq($mq-sm) {
      align-self: flex-start;
    }
  }

  &__prices {
    margin-bottom: $grid-base;

    @include mq($mq-sm) {
      margin-bottom: $grid-base * 2;
    }
  }
}
</style>

<style>
.sticky-cart {
  padding-bottom: 61px;
}

@media (min-width: 1024px) {
  .sticky-cart {
    padding-bottom: 0;
  }
}
</style>
